import useStyles from './AdditionalDetailsNotEditableStyles';
import useStylesCommon from '../common/AdditionalDetailsCommonStyles';
import {useTranslation} from 'react-i18next';
import i18nNamespaces from '../../../../const/i18nNamespaces';
import {Appointment} from '../../../../types/appointments';
import {Box, IconButton, List, ListItem} from '@material-ui/core';
import clsx from 'clsx';
import {Loading, UploadedItem} from '../../../index';
import React, {useEffect, useMemo, useState} from 'react';
import {
    useAppDispatch,
    useAppSelector,
} from '../../../../hooks/customReduxHooks';
import {selectAllAttachmentsMetaData, selectAttachmentsDownloadStatus, selectAttachmentsUploadStatus} from '../../../../store/appointmentAttachmentsSlice';
import {useOpenOrDownloadAsync} from '../../../../hooks/appointmentFileUpload';
import {getFileIcon} from '../../../../utils/file';
import appointmentsProvider from '../../../../services/appointmentsProvider';
import filesDownloadProvider from '../../../../services/filesDownloadProvider';
import {CustomAlert} from '../../../common/feedback';
import PrescriptionsListItem from '../../../prescription/PrescriptionsListItem/PrescriptionsListItem';
import {
    fetchPrescriptions,
    selectPrescriptions,
    selectPrescriptionsStatus,
} from '../../../../store/prescriptionSlice';
import {
    fetchMedicalRecord,
    selectMedicalRecord,
    selectMedicalRecordStatus
} from "../../../../store/medicalRecordSlice";
import kedZLAApi from "../../../../api/zusZLA/kedZLAApi";
import {IssuedZLADto} from "../../../../types/sickLeave";
import SickLeavesListItem from "../../../sickLeave/SickLeaveListItem/SickLeavesListItem";
import MedicalRecordFile from "../../../medicalRecord/MedicalRecordFile/MedicalRecordFile";
import {GetApp} from "@material-ui/icons";
import { getPreparedLang } from '../../../../utils/lang';
import PatientDiagnosticOrdersList from '../PatientDiagnosticOrdersList/PatientDiagnosticOrdersList';
import PatientReferralsList from '../PatientReferralsList/PatientReferralsList';
import { fetchReferrals, selectReferrals, selectReferralsStatus } from '../../../../store/referralSlice';
import PatientP1ReferralsList from '../PatientP1ReferralsList/PatientP1ReferralsList';
import QuestionToMedic from '../QuestionToMedic/QuestionToMedic';

interface Props {
    appointment: Appointment;
}

const AdditionalDetailsNotEditable = ({appointment}: Props) => {
    const classes = useStyles();
    const classesCommon = useStylesCommon();
    const {t} = useTranslation(i18nNamespaces.APPOINTMENT_DETAILS);
    const openOrDownloadFileAsync = useOpenOrDownloadAsync();
    const {t: tCommon, i18n} = useTranslation(i18nNamespaces.COMMON);
    const allAttachmentsMetaData = useAppSelector(selectAllAttachmentsMetaData);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const dispatch = useAppDispatch();
    const prescriptionsStatus = useAppSelector(selectPrescriptionsStatus);
    const prescriptions = useAppSelector(selectPrescriptions);
    const medicalRecordStatus = useAppSelector(selectMedicalRecordStatus);
    const medicalRecord = useAppSelector(selectMedicalRecord);
    const currentLanguage = i18n.language;
    const [sickLeaves, setSickLeaves] = useState<IssuedZLADto[]>([]);
    const attachmentsDownloadStatus = useAppSelector(selectAttachmentsDownloadStatus);
    const attachmentUploadStatus = useAppSelector(selectAttachmentsUploadStatus);
    const referralsStatus = useAppSelector(selectReferralsStatus);
    const referrals = useAppSelector(selectReferrals);
    const [questionSent, setQuestionSent] = useState(false);


    const appointmentNotes = useMemo(
        () => [
            {
                header: `${t('diagnosis')}`,
                note: getPreparedLang(currentLanguage) === 'pl' ?
                    appointment?.icd10Code?.text?.pl :
                    appointment?.icd10Code?.text?.en,
                download: false,
            },
            {
                header: `${t('proceduresPerformed')}`,
                note: `${appointment?.medicalService.name} - ${t('teleconsultation')}`,
                download: true,
            },
            {
                header: `${t('recommendations')}`,
                note:
                    (appointment?.drugMedicalRecommendations ||
                    appointment?.otherMedicalRecommendations ||
                    appointment?.referralMedicalRecommendations) ? (
                    <>
                        <p>{appointment?.drugMedicalRecommendations}</p>
                        <p>{appointment?.otherMedicalRecommendations}</p>
                        <p>{appointment?.referralMedicalRecommendations}</p>
                    </>
                ) : tCommon('noInformation'),
                download: true,
            },
        ],
        [appointment, currentLanguage],
    );

    const downlaodMedicRecommendations = async () => {
        try {
            setErrorMessage('');
            setLoading(true);
            const {data} = await appointmentsProvider.getRecommendations(
                appointment.id,
            );

            filesDownloadProvider.downloadFileFromUrl(
                data,
                `${t('recommendations')}.pdf`,
            );
        } catch (error) {
            setErrorMessage(t('downloadRecommendationError'));
        } finally {
            setLoading(false);
        }
    };

    const downlaodChatTranscription = async () => {
        try {
            setErrorMessage('');
            setLoading(true);
            const {data} = await appointmentsProvider.getChatTranscript(
                appointment.id,
            );

            filesDownloadProvider.downloadFileFromUrl(
                data,
                `${t('chatTranscription')}.pdf`,
            );
        } catch (error) {
            setErrorMessage(t('downloadRecommendationError'));
        } finally {
            setLoading(false);
        }
    };

    const fetchSickLeaves = async () => {
        try {
            const {data} = await kedZLAApi.getSickLeavesByAppointmentId(parseInt(appointment.id));
            if(data.isSuccess){
                setSickLeaves(data.result);
                // setCount (data.result.count);
            }
        } catch(error){
            console.error(error);
        }
    }

    const handleSendQuestion = () => {
        setQuestionSent(true);
    }

    useEffect(() => {
        dispatch(fetchReferrals({appointmentId: parseInt(appointment.id)}));
        dispatch(
            fetchPrescriptions({appointmentId: parseInt(appointment?.id)}),
        );
        dispatch(
            fetchMedicalRecord({appointmentId: parseInt(appointment?.id)}),
        );
        fetchSickLeaves();
    }, []);



    return (
        <>
            <Loading
                loading={loading || prescriptionsStatus === 'loading' || attachmentsDownloadStatus === 'loading' || attachmentUploadStatus === 'loading'}
                withBackdrop
            />
            {errorMessage && (
                <CustomAlert severity="error" message={errorMessage} />
            )}
            {appointmentNotes.map((appointmentNote, index) => (
                <Box className={`${classes.reasonsContainer}`} key={index}>
                    <Box className={classes.headerContainer}>
                        <h4 className={classesCommon.detailsHeader}>
                            {appointmentNote.header}
                        </h4>
                    </Box>
                    <Box className={classes.reasons}>
                        {appointmentNote.note || tCommon('noInformation')}
                    </Box>
                </Box>
            ))}
            <Box className={classes.divider} />
            <Box style={{marginBottom: 20, marginTop: 20}}>
                <h4 className={`${classesCommon.detailsHeader}`}>
                    {t('prescriptions')}
                </h4>
                <Box className={classes.listWrapper}>
                    {prescriptions?.length > 0
                        ? prescriptions?.map((prescriptionSet, idx) => (
                              <PrescriptionsListItem
                                  index={idx}
                                  key={idx}
                                  prescriptionSet={prescriptionSet}
                                  downloadable
                              />
                          ))
                        : tCommon('notIssued')}
                </Box>
            </Box>
            <Box style={{marginBottom: 20, marginTop: 20}}>
                <h4 className={`${classesCommon.detailsHeader}`}>
                    {t('sickLeaves')}
                </h4>
                <Box className={classes.listWrapper}>
                    {sickLeaves?.length > 0
                        ? sickLeaves.map((sickLeave, idx) => (
                            <SickLeavesListItem key={sickLeave.seriesAndNumber} index={idx} sickLeave={sickLeave} />
                    )) : tCommon('notIssued')}
                </Box>
            </Box>
            {referrals.length > 0 && (
                <PatientP1ReferralsList referrals={referrals} />
            )}
            {appointment?.diagnosticOrderHeaders?.length > 0 && (
                <PatientDiagnosticOrdersList diagnosticOrders={appointment?.diagnosticOrderHeaders} />
            )}
            {appointment?.referrals?.length > 0 && (
                <PatientReferralsList referrals={appointment?.referrals} />
            )}
            <Box>
                <h4 className={`${classesCommon.detailsHeader}`}>
                    {t('appointmentAttachments')}
                </h4>
                <Box className={classes.listWrapper}>
                    {allAttachmentsMetaData &&
                    allAttachmentsMetaData.length > 0 ? (
                        <List className={classesCommon.filesList}>
                            {allAttachmentsMetaData.map(
                                (attachmentMetaData, i) => (
                                    <ListItem
                                        style={{width: '100%', display: 'flex', justifyContent: 'space-between'}}
                                        className={clsx(
                                            classesCommon.filesListItem,
                                            {
                                                [classesCommon.filesListItemNext]:
                                                    i > 0,
                                            },
                                        )}
                                        onClick={() =>
                                            openOrDownloadFileAsync(
                                                appointment.id,
                                                `${attachmentMetaData.id}`,
                                            )
                                        }
                                        key={attachmentMetaData.id}
                                    >
                                        <UploadedItem
                                            fileName={
                                                attachmentMetaData.fileName
                                            }
                                            fileOwner={
                                                attachmentMetaData.person
                                            }
                                            fileIcon={getFileIcon(
                                                attachmentMetaData.contentType,
                                                attachmentMetaData.thumbnail,
                                            )}
                                            fileDateCreated={attachmentMetaData.uploadDate.toFormat(
                                                "dd'.'MM'.'yyyy",
                                            )}
                                            fileTimeCreated={attachmentMetaData.uploadDate.toFormat(
                                                "HH':'mm",
                                            )}
                                            removeFileEnabled={false}
                                        />
                                        <IconButton
                                            size={'small'}
                                            onClick={() =>
                                            openOrDownloadFileAsync(
                                                appointment.id,
                                                `${attachmentMetaData.id}`,
                                            )
                                        }>
                                            <GetApp color={'secondary'} fontSize={'small'}/>
                                        </IconButton>
                                    </ListItem>
                                ),
                            )}
                        </List>
                    ) : (
                        <p>{tCommon('noFilesAttached')}</p>
                    )}
                </Box>
            </Box>
            <Box style={{marginBottom: 20, marginTop: 20}}>
                <h4 className={`${classesCommon.detailsHeader}`}>
                    {t('medicalRecords')}
                </h4>
                <Box className={classes.listWrapper}>
                    {medicalRecord ?
                        <MedicalRecordFile />
                        : tCommon('recommendationWasNotGenerated')}
                </Box>
            </Box>

            {appointment.type !== 'Phone' && (
                <Box className={`${classes.reasonsContainer}`}>
                    <p className={classes.headerContainer}>
                        <h4 className={`${classesCommon.detailsHeader}`}>
                            {t('transcription')}
                        </h4>
                    </p>

                    {appointment.chatTranscriptionIsAvailable ? (
                        <Box
                            style={{
                                cursor: 'pointer',
                                display: 'flex',
                                justifyContent: 'space-between',
                            }}
                            onClick={() => downlaodChatTranscription()}
                        >
                            <UploadedItem
                                fileName={t('transcription')}
                                fileOwner={
                                    ""
                                }
                                fileIcon={getFileIcon('application/pdf')}
                                removeFileEnabled={false}
                            />
                            <IconButton
                                size={'small'}
                                onClick={downlaodChatTranscription}>
                                <GetApp color={'secondary'} fontSize={'small'}/>
                            </IconButton>
                        </Box>
                    ) : (
                        <p className={classes.reasons}>
                            {tCommon('recommendationWasNotGenerated')}
                        </p>
                    )}
                </Box>
            )}
            {!questionSent && appointment?.canPatientAskQuestion && (
                <>
                    <Box className={classes.divider} />
                    <Box style={{ marginTop: 20}}>
                        <QuestionToMedic appointmentId={appointment.id} onSuccess={handleSendQuestion}/>
                    </Box>
                </>
            )}
            
        </>
    );
};

export default AdditionalDetailsNotEditable;
